/**
 * MARKETING SITE APP.TSX - Used for Netlify deployment
 * 
 * This App.tsx is for the PUBLIC MARKETING SITE ONLY (evidenttrail.com)
 * - Used by: npm run dev:marketing (port 5176)
 * - Deploys to: Netlify
 * - Contains ONLY public routes (no authentication)
 * 
 * For main application routes (authenticated features), edit ui/src/App.tsx instead.
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { appLinks } from '@original/lib/crossSiteLinks';

// Marketing pages - import from original source
import LandingPage from '@original/pages/LandingPage';
import FAQPage from '@original/pages/FAQPage';

// SEO Pages
import GitHubChangeControlPage from '@original/pages/seo/GitHubChangeControlPage';
import SOC2ChangeManagementEvidencePage from '@original/pages/seo/SOC2ChangeManagementEvidencePage';
import Iso27001A832EvidencePage from '@original/pages/seo/Iso27001A832EvidencePage';
import GitHubCopilotCompliancePage from '@original/pages/seo/GitHubCopilotCompliancePage';
import SOC2GitHubCompliancePage from '@original/pages/seo/SOC2GitHubCompliancePage';
import Iso27001VsSoc2Page from '@original/pages/seo/Iso27001VsSoc2Page';
import Iso27001GitHubCompliancePage from '@original/pages/seo/Iso27001GitHubCompliancePage';
import EuAiActGitHubCompliancePage from '@original/pages/seo/EuAiActGitHubCompliancePage';
import OutsourcedSoftwareChangeControlPage from '@original/pages/seo/OutsourcedSoftwareChangeControlPage';
import ReadOnlyGitHubAuditPage from '@original/pages/seo/ReadOnlyGitHubAuditPage';
import AiAssistedDevelopmentEvidencePage from '@original/pages/seo/AiAssistedDevelopmentEvidencePage';
import AuditReadinessPage from '@original/pages/seo/AuditReadinessPage';
import BranchProtectionDriftDetectionPage from '@original/pages/seo/BranchProtectionDriftDetectionPage';
import CiEvidencePage from '@original/pages/seo/CiEvidencePage';
import WorkItemLinkingPage from '@original/pages/seo/WorkItemLinkingPage';
import ExceptionTrackingPage from '@original/pages/seo/ExceptionTrackingPage';
import SecureEvidenceSharingPage from '@original/pages/seo/SecureEvidenceSharingPage';
import GitHubWorkflowDriftDetectionPage from '@original/pages/seo/GitHubWorkflowDriftDetectionPage';

// Feature Pages
import FeaturesPage from '@original/pages/features/FeaturesPage';
import AuditTimelineFeaturePage from '@original/pages/features/AuditTimelineFeaturePage';
import DriftDetectionFeaturePage from '@original/pages/features/DriftDetectionFeaturePage';
import ExceptionTrackingFeaturePage from '@original/pages/features/ExceptionTrackingFeaturePage';

// Sample Pages
import SampleTimelinePage from '@original/pages/samples/SampleTimelinePage';
import SampleDriftEventPage from '@original/pages/samples/SampleDriftEventPage';
import SampleExceptionPage from '@original/pages/samples/SampleExceptionPage';

// Solutions Hub and Marketing Pages
import SolutionsHubPage from '@original/pages/marketing/SolutionsHubPage';
import ContinuousAuditEvidencePage from '@original/pages/marketing/ContinuousAuditEvidencePage';
import EngineeringAuditToilPage from '@original/pages/marketing/EngineeringAuditToilPage';
import AuditorSelfServePage from '@original/pages/marketing/AuditorSelfServePage';

// Tools Pages
import ToolsIndexPage from '@original/pages/tools/ToolsIndexPage';
import { PrApprovalsPage } from '@original/pages/tools/PrApprovalsPage';
import { BranchProtectionPage } from '@original/pages/tools/BranchProtectionPage';
import AiRiskClassifierPage from '@original/pages/tools/AiRiskClassifierPage';

// Legal Pages
import PrivacyPolicyPage from '@original/pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from '@original/pages/legal/TermsOfServicePage';
import SecurityPage from '@original/pages/legal/SecurityPage';

// 404 Page
import NotFoundPage from '@original/pages/NotFoundPage';

/** Redirect to external URL (cross-site) */
function ExternalRedirect({ to }: { to: string }) {
  window.location.href = to;
  return null;
}

function App() {
  return (
    <Routes>
      {/* Homepage */}
      <Route path="/" element={<LandingPage />} />

      {/* FAQ Page */}
      <Route path="/faq" element={<FAQPage />} />

      {/* SEO Pages */}
      <Route path="/github-change-control" element={<GitHubChangeControlPage />} />
      <Route path="/soc2-change-management-evidence" element={<SOC2ChangeManagementEvidencePage />} />
      <Route path="/outsourced-software-change-control" element={<OutsourcedSoftwareChangeControlPage />} />
      <Route path="/read-only-github-audit" element={<ReadOnlyGitHubAuditPage />} />
      <Route path="/ai-assisted-development-evidence" element={<AiAssistedDevelopmentEvidencePage />} />
      <Route path="/software-change-control-audit-evidence" element={<Navigate to="/audit-readiness" replace />} />
      <Route path="/audit-readiness" element={<AuditReadinessPage />} />
      <Route path="/change-control-evidence" element={<Navigate to="/github-change-control" replace />} />
      <Route path="/branch-protection-monitoring" element={<Navigate to="/branch-protection-drift-detection" replace />} />
      <Route path="/branch-protection-drift-detection" element={<BranchProtectionDriftDetectionPage />} />
      <Route path="/ci-evidence" element={<CiEvidencePage />} />
      <Route path="/work-item-linking" element={<WorkItemLinkingPage />} />
      <Route path="/exception-tracking" element={<ExceptionTrackingPage />} />
      <Route path="/eu-ai-act-software-change-control" element={<Navigate to="/eu-ai-act-github-compliance" replace />} />
      <Route path="/secure-evidence-sharing" element={<SecureEvidenceSharingPage />} />
      <Route path="/github-workflow-drift-detection" element={<GitHubWorkflowDriftDetectionPage />} />
      <Route path="/solutions/iso-27001-a832-evidence" element={<Iso27001A832EvidencePage />} />
      <Route path="/github-copilot-compliance" element={<GitHubCopilotCompliancePage />} />
      <Route path="/soc2-github-compliance" element={<SOC2GitHubCompliancePage />} />
      <Route path="/iso-27001-vs-soc2" element={<Iso27001VsSoc2Page />} />
      <Route path="/iso-27001-github-compliance" element={<Iso27001GitHubCompliancePage />} />
      <Route path="/eu-ai-act-github-compliance" element={<EuAiActGitHubCompliancePage />} />

      {/* Feature Pages */}
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/features/audit-timeline" element={<AuditTimelineFeaturePage />} />
      <Route path="/features/drift-detection" element={<DriftDetectionFeaturePage />} />
      <Route path="/features/exception-tracking" element={<ExceptionTrackingFeaturePage />} />

      {/* Sample Pages */}
      <Route path="/sample-timeline" element={<SampleTimelinePage />} />
      <Route path="/sample-drift-event" element={<SampleDriftEventPage />} />
      <Route path="/sample-exception" element={<SampleExceptionPage />} />

      {/* Solutions Hub */}
      <Route path="/solutions" element={<SolutionsHubPage />} />
      <Route path="/solutions/iso-27001-evidence" element={<Navigate to="/iso-27001-github-compliance" replace />} />
      <Route path="/continuous-audit-evidence" element={<ContinuousAuditEvidencePage />} />
      <Route path="/engineering-audit-toil" element={<EngineeringAuditToilPage />} />
      <Route path="/auditor-self-serve" element={<AuditorSelfServePage />} />

      {/* Tools Pages - Guest access */}
      <Route path="/tools" element={<ToolsIndexPage />} />
      <Route path="/tools/pr-approvals-report" element={<PrApprovalsPage />} />
      <Route path="/tools/branch-protection-snapshot" element={<BranchProtectionPage />} />
      <Route path="/tools/ai-risk-classifier" element={<AiRiskClassifierPage />} />

      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/security" element={<SecurityPage />} />

      {/* Redirect legacy login/register to app subdomain */}
      <Route path="/login" element={<ExternalRedirect to={appLinks.login} />} />
      <Route path="/register" element={<ExternalRedirect to={appLinks.register} />} />

      {/* Catch all - 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
